import React from 'react';
import { motion } from 'framer-motion';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Linkedin, ArrowRight } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import NotFoundPage from './NotFoundPage';
import { TEAM } from '../data/team';
import { isEnglishPath } from '../utils/i18nRoutes';

const TEXT = {
  es: {
    bioLabel: 'Bio',
    linksLabel: 'Enlaces',
    allTeam: 'Todo el equipo',
    david: 'David Redondo Pérez',
    cta: 'Hablar con el equipo',
    breadcrumbHome: 'Inicio',
    breadcrumbTeam: 'Equipo',
  },
  en: {
    bioLabel: 'Bio',
    linksLabel: 'Links',
    allTeam: 'Full team',
    david: 'David Redondo Pérez',
    cta: 'Talk to the team',
    breadcrumbHome: 'Home',
    breadcrumbTeam: 'Team',
  },
};

// Generic profile page for team members other than David (who has a bespoke page).
// Reads the :slug param and looks up the shared team data. Supports both the ES
// (/equipo/:slug) and EN (/en/team/:slug) routes.
export default function TeamMemberPage() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const member = TEAM.find((m) => m.slug === slug && m.slug !== 'david-redondo-perez');

  if (!member) return <NotFoundPage />;

  const t = TEXT[locale];
  const role = locale === 'en' ? member.roleEn : member.roleEs;
  const bio = locale === 'en' ? member.bioEn : member.bioEs;
  const teamPath = locale === 'en' ? '/en/team' : '/equipo';
  const davidPath = locale === 'en' ? '/en/team/david-redondo-perez' : '/equipo/david-redondo-perez';
  const contactPath = locale === 'en' ? '/en/contact' : '/contacto';
  const path = locale === 'en' ? `/en/team/${member.slug}` : `/equipo/${member.slug}`;

  const ALTERNATES = [
    { hreflang: 'es', href: `/equipo/${member.slug}` },
    { hreflang: 'en', href: `/en/team/${member.slug}` },
    { hreflang: 'x-default', href: `/equipo/${member.slug}` },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `https://innovation.valorizasm.com/equipo/${member.slug}#person`,
    name: member.name,
    jobTitle: member.roleEs,
    url: `https://innovation.valorizasm.com/equipo/${member.slug}`,
    worksFor: { '@id': 'https://innovation.valorizasm.com/#organization' },
    sameAs: [member.linkedin],
  };

  return (
    <>
      <SEOHead
        title={`${member.name} | ${role} | Valoriza`}
        description={bio}
        path={path}
        locale={locale}
        alternates={ALTERNATES}
        ogType="profile"
        jsonLd={[jsonLd]}
      />
      <article className="pt-32 pb-24 v-container" data-testid="team-member-page">
        <Breadcrumb items={[{ label: t.breadcrumbHome, to: locale === 'en' ? '/en' : '/' }, { label: t.breadcrumbTeam, to: teamPath }, { label: member.name }]} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-12">
          <div className="w-28 h-28 rounded-full flex-shrink-0 overflow-hidden" style={{ background: `${member.color}20`, border: `3px solid ${member.color}40` }}>
            <img src={member.photo} alt={`${member.name}, ${role}`} className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-black dark:text-white/95 mb-1 tracking-tight" data-testid="team-member-h1">{member.name}</h1>
            <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: member.color }}>{role}</p>
            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-white/60 hover:text-[#8DC21F] transition-colors" data-testid="team-member-linkedin-link">
              <Linkedin size={15} /> LinkedIn
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="text-xl font-bold text-black dark:text-white/90 mb-3">{t.bioLabel}</h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">{bio}</p>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-white/40 mb-3">{t.linksLabel}</h3>
              <div className="flex flex-col gap-2.5 text-sm">
                <Link to={teamPath} className="hover:text-[#8DC21F] transition-colors">{t.allTeam}</Link>
                <Link to={davidPath} className="hover:text-[#8DC21F] transition-colors">{t.david}</Link>
              </div>
            </div>
            <Link to={contactPath} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="team-member-cta-contact">
              {t.cta} <ArrowRight size={15} />
            </Link>
          </aside>
        </div>
      </article>
    </>
  );
}
